import React, { useState, useEffect, useRef } from 'react';
import { useAsset } from '../../context/AssetContext';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '../ui/dropdown-menu';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { Skeleton } from '../ui/skeleton';
import { ScrollArea } from '../ui/scroll-area';
import { Checkbox } from '../ui/checkbox';
import { toast } from 'sonner';
import { 
  Plus, Search, Upload, Download, MoreHorizontal, Pencil, Trash2, 
  UserPlus, UserMinus, Loader2, History, FileDown, FileUp, Info, Calendar, AlertTriangle, Settings2,
  ArrowUp, ArrowDown, ArrowUpDown, X, User as UserIcon
} from 'lucide-react';
import * as XLSX from 'xlsx';

// Column definitions (id = field key; sortable = whether to allow header sort)
const ALL_COLUMNS = [
  { id: 'asset_id', label: 'Asset ID', default: true, sortable: true },
  { id: 'asset_name', label: 'Name', default: true, sortable: true },
  { id: 'asset_type', label: 'Type', default: true, sortable: true },
  { id: 'status', label: 'Status', default: true, sortable: true },
  { id: 'allocated_to', label: 'Allocated To', default: true, sortable: true },
  { id: 'price', label: 'Price', default: true, sortable: true },
  { id: 'age', label: 'Age', default: true, sortable: true, sortField: 'invoice_date', sortInverted: true },
  { id: 'serial_no', label: 'Serial No', default: false, sortable: true },
  { id: 'invoice_no', label: 'Invoice No', default: false, sortable: true },
  { id: 'invoice_date', label: 'Invoice Date', default: false, sortable: true },
  { id: 'warranty_expiration_date', label: 'Warranty Exp.', default: false, sortable: true },
  { id: 'adp_expiration_date', label: 'ADP Exp.', default: false, sortable: true },
  { id: 'processor', label: 'Processor', default: false, sortable: true },
  { id: 'ram', label: 'RAM', default: false, sortable: true },
  { id: 'ssd_size', label: 'SSD Size', default: false, sortable: true },
];

const getDefaultColumns = () => {
  const saved = localStorage.getItem('assetListColumns');
  if (saved) {
    try {
      return JSON.parse(saved);
    } catch {
      return ALL_COLUMNS.filter(c => c.default).map(c => c.id);
    }
  }
  return ALL_COLUMNS.filter(c => c.default).map(c => c.id);
};

const AssetList = ({ onView, initialFilters, onClearInitialFilters }) => {
  const { 
    assets, loading, pagination, lovs, employees, isReady,
    fetchAssets, fetchEmployees, fetchLOVs, createAsset, updateAsset, deleteAsset, bulkDeleteAssets,
    allocateAsset, unallocateAsset, getAssetHistory, getAssetAllocationHistory, downloadAssetTemplate, importAssets, exportAssets
  } = useAsset();

  const fileInputRef = useRef(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [ageFilter, setAgeFilter] = useState('all');

  // Filters that come from the Dashboard tile/chart click (raw query params)
  const [dashboardFilters, setDashboardFilters] = useState(initialFilters || null);

  // Sorting state
  const [sortBy, setSortBy] = useState(null);
  const [sortOrder, setSortOrder] = useState('asc'); // 'asc' | 'desc'

  // Column visibility
  const [visibleColumns, setVisibleColumns] = useState(getDefaultColumns);
  const [columnSelectorOpen, setColumnSelectorOpen] = useState(false);

  // Dialogs
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [bulkDeleteDialogOpen, setBulkDeleteDialogOpen] = useState(false);
  const [allocateDialogOpen, setAllocateDialogOpen] = useState(false);
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [historyDialogOpen, setHistoryDialogOpen] = useState(false);

  const [selectedAsset, setSelectedAsset] = useState(null);
  const [selectedAssets, setSelectedAssets] = useState([]);
  const [formLoading, setFormLoading] = useState(false);

  const [formData, setFormData] = useState({
    asset_id: '',
    asset_name: '',
    asset_type: '',
    status: 'Available',
    serial_no: '',
    invoice_no: '',
    invoice_date: '',
    warranty_expiration_date: '',
    adp_expiration_date: '',
    processor: '',
    ram: '',
    ssd_size: '',
    windows_activation_key: '',
    price: ''
  });

  const [allocateData, setAllocateData] = useState({ asset_id: '', employee_id: '' });
  
  // Import state - enhanced
  const [importStep, setImportStep] = useState('info'); // 'info', 'preview'
  const [importPreview, setImportPreview] = useState({ valid: [], invalid: [], file: null });
  
  // History state
  const [historyData, setHistoryData] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  // Allocation History (allocations only) state
  const [allocHistoryDialogOpen, setAllocHistoryDialogOpen] = useState(false);
  const [allocHistoryData, setAllocHistoryData] = useState([]);
  const [allocHistoryLoading, setAllocHistoryLoading] = useState(false);

  // Age filter options
  const ageFilterOptions = [
    { value: 'all', label: 'All Ages' },
    { value: 'last_30_days', label: 'Last 30 Days' },
    { value: 'current_month', label: 'Current Month' },
    { value: 'current_quarter', label: 'Current Quarter' },
    { value: 'last_quarter', label: 'Last Quarter' },
    { value: 'current_year', label: 'Current Year' },
    { value: 'last_year', label: 'Last Year' }
  ];

  // Save column preferences
  useEffect(() => {
    localStorage.setItem('assetListColumns', JSON.stringify(visibleColumns));
  }, [visibleColumns]);

  const toggleColumn = (columnId) => {
    setVisibleColumns(prev => 
      prev.includes(columnId)
        ? prev.filter(id => id !== columnId)
        : [...prev, columnId]
    );
  };

  const resetColumns = () => {
    const defaultCols = ALL_COLUMNS.filter(c => c.default).map(c => c.id);
    setVisibleColumns(defaultCols);
  };

  const isColumnVisible = (columnId) => visibleColumns.includes(columnId);

  useEffect(() => {
    if (isReady) {
      loadAssets();
      fetchLOVs();
      fetchEmployees();
    }
  }, [isReady]);

  useEffect(() => {
    // When the parent passes a new initialFilters payload (from dashboard tile click),
    // adopt it as the active dashboard filter set
    if (initialFilters) {
      setDashboardFilters(initialFilters);
      setCurrentPage(1);
    }
  }, [initialFilters]);

  useEffect(() => {
    if (!isReady) return;
    const timer = setTimeout(() => {
      setCurrentPage(1);
      loadAssets(1);
    }, 300);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery, statusFilter, typeFilter, ageFilter, dashboardFilters, sortBy, sortOrder, isReady]);

  const loadAssets = async (page = currentPage) => {
    const filters = {};
    if (searchQuery) filters.search = searchQuery;
    if (statusFilter && statusFilter !== 'all') filters.status = statusFilter;
    if (typeFilter && typeFilter !== 'all') filters.asset_type = typeFilter;
    if (ageFilter && ageFilter !== 'all') filters.age_filter = ageFilter;
    // Merge dashboard-provided filters (warranty_filter, multi-status)
    if (dashboardFilters) {
      if (dashboardFilters.status && (!statusFilter || statusFilter === 'all')) {
        filters.status = dashboardFilters.status;
      }
      if (dashboardFilters.warranty_filter) {
        filters.warranty_filter = dashboardFilters.warranty_filter;
      }
    }
    if (sortBy) {
      filters.sort_by = sortBy;
      filters.sort_order = sortOrder;
    }
    await fetchAssets(filters, page, 50);
  };

  const clearDashboardFilters = () => {
    setDashboardFilters(null);
    if (onClearInitialFilters) onClearInitialFilters();
  };

  const handleSort = (col) => {
    if (!col || col.sortable === false) return;
    const field = col.sortField || col.id;
    if (sortBy === field) {
      if (sortOrder === 'asc') {
        setSortOrder('desc');
      } else {
        // third click clears sort
        setSortBy(null);
        setSortOrder('asc');
      }
    } else {
      setSortBy(field);
      setSortOrder(col.sortInverted ? 'desc' : 'asc');
    }
  };

  const renderSortIcon = (col) => {
    if (!col?.sortable) return null;
    const field = col.sortField || col.id;
    if (sortBy !== field) {
      return <ArrowUpDown className="h-3.5 w-3.5 ml-1 opacity-40" />;
    }
    return sortOrder === 'asc'
      ? <ArrowUp className="h-3.5 w-3.5 ml-1 text-primary" />
      : <ArrowDown className="h-3.5 w-3.5 ml-1 text-primary" />;
  };

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
    loadAssets(newPage);
  };

  const resetForm = () => {
    setFormData({
      asset_id: '', asset_name: '', asset_type: '', status: 'Available',
      serial_no: '', invoice_no: '', invoice_date: '', warranty_expiration_date: '',
      adp_expiration_date: '', processor: '', ram: '', ssd_size: '', windows_activation_key: '',
      price: ''
    });
  };

  const handleCreate = async () => {
    if (!formData.asset_id || !formData.asset_name || !formData.asset_type || !formData.status) {
      toast.error('Please fill in all required fields');
      return;
    }

    setFormLoading(true);
    const dataToSend = {
      ...formData,
      price: formData.price ? parseFloat(formData.price) : null
    };
    const result = await createAsset(dataToSend);
    setFormLoading(false);

    if (result.success) {
      toast.success('Asset created successfully');
      setCreateDialogOpen(false);
      resetForm();
      loadAssets();
    } else {
      toast.error(result.error);
    }
  };

  const handleEdit = async () => {
    setFormLoading(true);
    const { asset_id, ...updateData } = formData;
    const dataToSend = {
      ...updateData,
      price: updateData.price ? parseFloat(updateData.price) : null
    };
    const result = await updateAsset(selectedAsset.asset_id, dataToSend);
    setFormLoading(false);

    if (result.success) {
      toast.success('Asset updated successfully');
      setEditDialogOpen(false);
      resetForm();
      loadAssets();
    } else {
      toast.error(result.error);
    }
  };

  const handleDelete = async () => {
    setFormLoading(true);
    const result = await deleteAsset(selectedAsset.asset_id);
    setFormLoading(false);

    if (result.success) {
      toast.success('Asset deleted successfully');
      setDeleteDialogOpen(false);
      loadAssets();
    } else {
      toast.error(result.error);
    }
  };

  const handleBulkDelete = async () => {
    if (selectedAssets.length === 0) {
      toast.error('No assets selected');
      return;
    }

    setFormLoading(true);
    const result = await bulkDeleteAssets(selectedAssets);
    setFormLoading(false);

    if (result.success) {
      toast.success(`Deleted ${result.data.deleted_count} assets`);
      setBulkDeleteDialogOpen(false);
      setSelectedAssets([]);
      loadAssets();
    } else {
      toast.error(result.error);
    }
  };

  const toggleAssetSelection = (assetId) => {
    setSelectedAssets(prev => 
      prev.includes(assetId) 
        ? prev.filter(id => id !== assetId)
        : [...prev, assetId]
    );
  };

  const toggleSelectAll = () => {
    const selectableAssets = (assets || []).filter(a => !a.allocated_to);
    if (selectedAssets.length === selectableAssets.length) {
      setSelectedAssets([]);
    } else {
      setSelectedAssets(selectableAssets.map(a => a.asset_id));
    }
  };

  const handleAllocate = async () => {
    if (!allocateData.asset_id || !allocateData.employee_id) {
      toast.error('Please select both asset and employee');
      return;
    }

    setFormLoading(true);
    const result = await allocateAsset(allocateData.asset_id, allocateData.employee_id);
    setFormLoading(false);

    if (result.success) {
      toast.success('Asset allocated successfully');
      setAllocateDialogOpen(false);
      setAllocateData({ asset_id: '', employee_id: '' });
      loadAssets();
    } else {
      toast.error(result.error);
    }
  };

  const handleUnallocate = async (asset) => {
    const result = await unallocateAsset(asset.asset_id);
    if (result.success) {
      toast.success('Asset unallocated successfully');
      loadAssets();
    } else {
      toast.error(result.error);
    }
  };

  const openEditDialog = (asset) => {
    setSelectedAsset(asset);
    setFormData({ 
      ...asset,
      price: asset.price || ''
    });
    setEditDialogOpen(true);
  };

  const openDeleteDialog = (asset) => {
    setSelectedAsset(asset);
    setDeleteDialogOpen(true);
  };

  const openAllocateForAsset = (asset) => {
    setAllocateData({ asset_id: asset.asset_id, employee_id: '' });
    setAllocateDialogOpen(true);
  };

  const openHistoryDialog = async (asset) => {
    setSelectedAsset(asset);
    setHistoryLoading(true);
    setHistoryDialogOpen(true);
    
    const history = await getAssetHistory(asset.asset_id);
    setHistoryData(history);
    setHistoryLoading(false);
  };

  const openAllocHistoryDialog = async (asset) => {
    setSelectedAsset(asset);
    setAllocHistoryLoading(true);
    setAllocHistoryDialogOpen(true);
    const history = await getAssetAllocationHistory(asset.asset_id);
    setAllocHistoryData(history);
    setAllocHistoryLoading(false);
  };

  // Enhanced Import handling
  const openImportDialog = () => {
    setImportStep('info');
    setImportPreview({ valid: [], invalid: [], file: null });
    setImportDialogOpen(true);
  };

  const handleDownloadTemplate = async () => {
    setFormLoading(true);
    const result = await downloadAssetTemplate();
    setFormLoading(false);
    if (result.success) {
      toast.success('Template downloaded');
    } else {
      toast.error(result.error || 'Failed to download template');
    }
  };

  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = '';

    if (!file.name.endsWith('.xlsx') && !file.name.endsWith('.xls')) {
      toast.error('Please select an Excel file (.xlsx)');
      return;
    }

    try {
      const data = await file.arrayBuffer();
      const workbook = XLSX.read(data, { type: 'array', cellDates: true });
      const sheet = workbook.Sheets[workbook.SheetNames[0]];
      const rows = XLSX.utils.sheet_to_json(sheet, { raw: false, defval: '' });

      const valid = [];
      const invalid = [];

      rows.forEach((row, idx) => {
        const errors = [];
        if (!row.asset_id) errors.push('Asset ID required');
        if (!row.asset_type) errors.push('Asset Type required');
        if (!row.asset_name) errors.push('Asset Name required');
        if (!row.status) errors.push('Status required');

        if (errors.length > 0) {
          invalid.push({ row: idx + 2, data: row, errors });
        } else {
          valid.push(row);
        }
      });

      setImportPreview({ valid, invalid, file });
      setImportStep('preview');
    } catch (err) {
      toast.error('Failed to parse file');
    }
  };

  const handleImport = async () => {
    if (!importPreview.file) return;

    setFormLoading(true);
    const result = await importAssets(importPreview.file);
    setFormLoading(false);

    if (result.success) {
      toast.success(`Imported ${result.data.valid_count} assets`);
      setImportDialogOpen(false);
      setImportPreview({ valid: [], invalid: [], file: null });
      loadAssets();
    } else {
      toast.error(result.error);
    }
  };

  const handleExport = async () => {
    const result = await exportAssets();
    if (result.success) {
      toast.success('Assets exported successfully');
    } else {
      toast.error(result.error);
    }
  };

  const formatPrice = (price) => {
    if (!price && price !== 0) return '-';
    return new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(price);
  };

  const formatAge = (months, years) => {
    if (months === null || months === undefined) return '-';
    if (months < 12) return `${months}m`;
    return `${years}y`;
  };

  const showTechSpecs = formData.asset_type === 'LAPTOP' || formData.asset_type === 'DESKTOP';
  const unallocatedAssets = Array.isArray(assets) ? assets.filter(a => !a.allocated_to) : [];

  // Column info for import - added price
  const importColumns = [
    { name: 'asset_id', required: true, description: 'Unique identifier (e.g., LAPTOP001)' },
    { name: 'asset_type', required: true, description: 'Type from LOV (e.g., LAPTOP, MONITOR)' },
    { name: 'asset_name', required: true, description: 'Display name (e.g., Dell XPS 15)' },
    { name: 'status', required: true, description: 'Status from LOV (e.g., Available)' },
    { name: 'serial_no', required: false, description: 'Serial number' },
    { name: 'invoice_no', required: false, description: 'Invoice number' },
    { name: 'invoice_date', required: false, description: 'Date format: YYYY-MM-DD' },
    { name: 'warranty_expiration_date', required: false, description: 'Date format: YYYY-MM-DD' },
    { name: 'adp_expiration_date', required: false, description: 'ADP expiration date' },
    { name: 'processor', required: false, description: 'For LAPTOP/DESKTOP only' },
    { name: 'ram', required: false, description: 'For LAPTOP/DESKTOP only' },
    { name: 'ssd_size', required: false, description: 'For LAPTOP/DESKTOP only' },
    { name: 'windows_activation_key', required: false, description: 'Windows license key' },
    { name: 'price', required: false, description: 'Asset price (numeric value)' },
  ];

  return (
    <div className="space-y-6" data-testid="asset-list">
      <div className="flex flex-col sm:flex-row justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold tracking-tight">Assets</h2>
          <p className="text-muted-foreground">Manage your IT assets</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setAllocateDialogOpen(true)}>
            <UserPlus className="mr-2 h-4 w-4" /> Allocate
          </Button>
          <Button variant="outline" onClick={openImportDialog}>
            <Upload className="mr-2 h-4 w-4" /> Import
          </Button>
          <Button variant="outline" onClick={handleExport}>
            <Download className="mr-2 h-4 w-4" /> Export
          </Button>
          <Button onClick={() => setCreateDialogOpen(true)}>
            <Plus className="mr-2 h-4 w-4" /> Add Asset
          </Button>
        </div>
      </div>

      {/* Active Dashboard Filter Chip */}
      {dashboardFilters && (dashboardFilters.status || dashboardFilters.warranty_filter) && (
        <div className="flex items-center gap-2 px-1" data-testid="dashboard-filter-chip">
          <span className="text-sm text-muted-foreground">Filtered from Dashboard:</span>
          <Badge variant="secondary" className="gap-1 pr-1">
            {dashboardFilters.status && `Status: ${dashboardFilters.status}`}
            {dashboardFilters.warranty_filter === 'expired' && 'Warranty: Out of Warranty'}
            {dashboardFilters.warranty_filter === 'expiring_soon' && 'Warranty: Expiring Soon'}
            <Button
              variant="ghost"
              size="icon"
              className="h-4 w-4 ml-1 hover:bg-transparent"
              onClick={clearDashboardFilters}
              data-testid="dashboard-filter-clear-btn"
            >
              <X className="h-3 w-3" />
            </Button>
          </Badge>
        </div>
      )}

      {/* Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search by Asset ID, Name, or Serial No..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[150px]">
                <SelectValue placeholder="All Statuses" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                {lovs.asset_statuses.map(s => (
                  <SelectItem key={s} value={s}>{s}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={typeFilter} onValueChange={setTypeFilter}>
              <SelectTrigger className="w-[150px]">
                <SelectValue placeholder="All Types" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                {lovs.asset_types.map(t => (
                  <SelectItem key={t} value={t}>{t}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={ageFilter} onValueChange={setAgeFilter}>
              <SelectTrigger className="w-[170px]">
                <Calendar className="mr-2 h-4 w-4" />
                <SelectValue placeholder="All Ages" />
              </SelectTrigger>
              <SelectContent>
                {ageFilterOptions.map(opt => (
                  <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            {/* Column Selector */}
            <Popover open={columnSelectorOpen} onOpenChange={setColumnSelectorOpen}>
              <PopoverTrigger asChild>
                <Button variant="outline" size="icon" title="Select Columns">
                  <Settings2 className="h-4 w-4" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-[250px] p-0" align="end">
                <div className="p-3 border-b">
                  <div className="flex items-center justify-between">
                    <h4 className="font-medium text-sm">Visible Columns</h4>
                    <Button variant="ghost" size="sm" onClick={resetColumns} className="h-7 text-xs">
                      Reset
                    </Button>
                  </div>
                </div>
                <ScrollArea className="h-[300px]">
                  <div className="p-2">
                    {ALL_COLUMNS.map((col) => (
                      <div
                        key={col.id}
                        className="flex items-center space-x-2 py-2 px-2 hover:bg-muted rounded-sm cursor-pointer"
                        onClick={() => toggleColumn(col.id)}
                      >
                        <Checkbox
                          checked={isColumnVisible(col.id)}
                          onCheckedChange={() => toggleColumn(col.id)}
                        />
                        <span className="text-sm">{col.label}</span>
                      </div>
                    ))}
                  </div>
                </ScrollArea>
              </PopoverContent>
            </Popover>
          </div>
        </CardContent>
      </Card>

      {/* Assets Table */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <span>Assets</span>
              {selectedAssets.length > 0 && (
                <Button 
                  variant="destructive" 
                  size="sm" 
                  onClick={() => setBulkDeleteDialogOpen(true)}
                >
                  <Trash2 className="mr-2 h-4 w-4" />
                  Delete Selected ({selectedAssets.length})
                </Button>
              )}
            </div>
            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground">{visibleColumns.length} columns</span>
              <Badge variant="secondary">{pagination?.total || 0} records</Badge>
            </div>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="space-y-2">
              {[1, 2, 3, 4, 5].map(i => <Skeleton key={i} className="h-12 w-full" />)}
            </div>
          ) : !Array.isArray(assets) || assets.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              No assets found. Add your first asset to get started.
            </div>
          ) : (
            <div className="rounded-md border overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-[50px]">
                      <Checkbox
                        checked={selectedAssets.length > 0 && selectedAssets.length === assets.filter(a => !a.allocated_to).length}
                        onCheckedChange={toggleSelectAll}
                        aria-label="Select all unallocated"
                      />
                    </TableHead>
                    {ALL_COLUMNS.filter(c => isColumnVisible(c.id)).map(col => (
                      <TableHead
                        key={col.id}
                        className={col.sortable ? 'cursor-pointer select-none hover:bg-muted/50' : ''}
                        onClick={() => handleSort(col)}
                        data-testid={`asset-col-header-${col.id}`}
                      >
                        <span className="inline-flex items-center">
                          {col.label}
                          {renderSortIcon(col)}
                        </span>
                      </TableHead>
                    ))}
                    <TableHead className="w-[50px]"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                {assets.map((asset) => (
                    <TableRow
                      key={asset?.asset_id || Math.random()}
                      className={onView ? 'cursor-pointer hover:bg-muted/50' : ''}
                      data-testid={`asset-row-${asset?.asset_id}`}
                      onClick={(e) => {
                        // Avoid hijacking clicks on the checkbox/actions column
                        const tag = e.target.tagName?.toLowerCase();
                        if (tag === 'input' || tag === 'button' || tag === 'svg' || tag === 'path') return;
                        if (e.target.closest('button') || e.target.closest('[role="menu"]') || e.target.closest('[role="checkbox"]')) return;
                        if (onView && asset?.asset_id) onView(asset);
                      }}
                    >
                      <TableCell onClick={(e) => e.stopPropagation()}>
                        <Checkbox
                          checked={selectedAssets.includes(asset?.asset_id)}
                          onCheckedChange={() => toggleAssetSelection(asset?.asset_id)}
                          disabled={!!asset?.allocated_to}
                          aria-label={`Select ${asset?.asset_id}`}
                        />
                      </TableCell>
                      {ALL_COLUMNS.filter(c => isColumnVisible(c.id)).map(col => {
                        if (col.id === 'asset_id') {
                          return (
                            <TableCell key={col.id} className="font-mono">
                              {onView ? (
                                <button
                                  type="button"
                                  className="text-primary hover:underline"
                                  onClick={(e) => { e.stopPropagation(); onView(asset); }}
                                  data-testid={`asset-id-link-${asset?.asset_id}`}
                                >
                                  {asset?.asset_id}
                                </button>
                              ) : (
                                asset?.asset_id
                              )}
                            </TableCell>
                          );
                        }
                        if (col.id === 'asset_name') {
                          return <TableCell key={col.id} className="font-medium">{asset?.asset_name || '-'}</TableCell>;
                        }
                        if (col.id === 'asset_type') {
                          return <TableCell key={col.id}><Badge variant="outline">{asset?.asset_type || '-'}</Badge></TableCell>;
                        }
                        if (col.id === 'status') {
                          return (
                            <TableCell key={col.id}>
                              <Badge variant={asset?.status === 'Available' ? 'default' : asset?.status === 'Allocated' ? 'secondary' : 'outline'}>
                                {asset?.status || '-'}
                              </Badge>
                            </TableCell>
                          );
                        }
                        if (col.id === 'allocated_to') {
                          return <TableCell key={col.id}>{asset?.allocated_to || '-'}</TableCell>;
                        }
                        if (col.id === 'price') {
                          return <TableCell key={col.id} className="font-mono text-sm">{formatPrice(asset?.price)}</TableCell>;
                        }
                        if (col.id === 'age') {
                          return (
                            <TableCell key={col.id}>
                              <span className="text-sm" title={`${asset?.age_months || 0} months`}>
                                {formatAge(asset?.age_months, asset?.age_years)}
                              </span>
                            </TableCell>
                          );
                        }
                        // Generic text columns
                        const val = asset?.[col.id];
                        return (
                          <TableCell key={col.id} className={['serial_no', 'invoice_no'].includes(col.id) ? 'font-mono text-sm' : 'text-sm'}>
                            {val || '-'}
                          </TableCell>
                        );
                      })}
                      <TableCell onClick={(e) => e.stopPropagation()}>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon"><MoreHorizontal className="h-4 w-4" /></Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            {onView && (
                              <DropdownMenuItem onClick={() => onView(asset)} data-testid={`asset-action-view-${asset?.asset_id}`}>
                                <Info className="mr-2 h-4 w-4" /> View Details
                              </DropdownMenuItem>
                            )}
                            <DropdownMenuItem onClick={() => openEditDialog(asset)}>
                              <Pencil className="mr-2 h-4 w-4" /> Edit
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => openAllocHistoryDialog(asset)} data-testid={`asset-action-alloc-history-${asset?.asset_id}`}>
                              <UserIcon className="mr-2 h-4 w-4" /> Allocation History
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => openHistoryDialog(asset)} data-testid={`asset-action-update-history-${asset?.asset_id}`}>
                              <History className="mr-2 h-4 w-4" /> Asset Update History
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            {!asset?.allocated_to ? (
                              <DropdownMenuItem onClick={() => openAllocateForAsset(asset)}>
                                <UserPlus className="mr-2 h-4 w-4" /> Allocate
                              </DropdownMenuItem>
                            ) : (
                              <DropdownMenuItem onClick={() => handleUnallocate(asset)}>
                                <UserMinus className="mr-2 h-4 w-4" /> Unallocate
                              </DropdownMenuItem>
                            )}
                            <DropdownMenuSeparator />
                            <DropdownMenuItem onClick={() => openDeleteDialog(asset)} className="text-red-600">
                              <Trash2 className="mr-2 h-4 w-4" /> Delete
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}

          {/* Pagination */}
          {!loading && Array.isArray(assets) && assets.length > 0 && pagination?.total_pages > 1 && (
            <div className="flex items-center justify-between mt-4 pt-4 border-t">
              <p className="text-sm text-muted-foreground">
                Page {pagination.page} of {pagination.total_pages}
              </p>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={() => handlePageChange(pagination.page - 1)} disabled={pagination.page <= 1}>
                  Previous
                </Button>
                <Button variant="outline" size="sm" onClick={() => handlePageChange(pagination.page + 1)} disabled={pagination.page >= pagination.total_pages}>
                  Next
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create/Edit Dialog */}
      <Dialog open={createDialogOpen || editDialogOpen} onOpenChange={(open) => {
        if (!open) { setCreateDialogOpen(false); setEditDialogOpen(false); resetForm(); }
      }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editDialogOpen ? 'Edit Asset' : 'Add New Asset'}</DialogTitle>
            <DialogDescription>Fill in the asset details below</DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Asset ID *</Label>
                <Input value={formData.asset_id} onChange={(e) => setFormData({ ...formData, asset_id: e.target.value })} disabled={editDialogOpen} placeholder="e.g., LAPTOP001" />
              </div>
              <div className="space-y-2">
                <Label>Asset Name *</Label>
                <Input value={formData.asset_name} onChange={(e) => setFormData({ ...formData, asset_name: e.target.value })} placeholder="e.g., Dell XPS 15" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Asset Type *</Label>
                <Select value={formData.asset_type} onValueChange={(v) => setFormData({ ...formData, asset_type: v })}>
                  <SelectTrigger><SelectValue placeholder="Select type" /></SelectTrigger>
                  <SelectContent>
                    {lovs.asset_types.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Status *</Label>
                <Select value={formData.status} onValueChange={(v) => setFormData({ ...formData, status: v })}>
                  <SelectTrigger><SelectValue placeholder="Select status" /></SelectTrigger>
                  <SelectContent>
                    {lovs.asset_statuses.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Serial Number</Label>
                <Input value={formData.serial_no} onChange={(e) => setFormData({ ...formData, serial_no: e.target.value })} />
              </div>
              <div className="space-y-2">
                <Label>Price (₹)</Label>
                <Input 
                  type="number" 
                  value={formData.price} 
                  onChange={(e) => setFormData({ ...formData, price: e.target.value })} 
                  placeholder="e.g., 125000"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Invoice Number</Label>
                <Input value={formData.invoice_no} onChange={(e) => setFormData({ ...formData, invoice_no: e.target.value })} />
              </div>
              <div className="space-y-2">
                <Label>Invoice Date</Label>
                <Input type="date" value={formData.invoice_date} onChange={(e) => setFormData({ ...formData, invoice_date: e.target.value })} />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Warranty Expiration</Label>
                <Input type="date" value={formData.warranty_expiration_date} onChange={(e) => setFormData({ ...formData, warranty_expiration_date: e.target.value })} />
              </div>
              <div className="space-y-2">
                <Label>ADP Expiration</Label>
                <Input type="date" value={formData.adp_expiration_date} onChange={(e) => setFormData({ ...formData, adp_expiration_date: e.target.value })} />
              </div>
            </div>
            {showTechSpecs && (
              <>
                <div className="border-t pt-4 mt-2">
                  <h4 className="font-medium mb-4">Technical Specifications (LAPTOP/DESKTOP only)</h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Processor</Label>
                      <Input value={formData.processor} onChange={(e) => setFormData({ ...formData, processor: e.target.value })} placeholder="e.g., Intel i7-12700H" />
                    </div>
                    <div className="space-y-2">
                      <Label>RAM</Label>
                      <Input value={formData.ram} onChange={(e) => setFormData({ ...formData, ram: e.target.value })} placeholder="e.g., 16GB" />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4 mt-4">
                    <div className="space-y-2">
                      <Label>SSD Size</Label>
                      <Input value={formData.ssd_size} onChange={(e) => setFormData({ ...formData, ssd_size: e.target.value })} placeholder="e.g., 512GB" />
                    </div>
                    <div className="space-y-2">
                      <Label>Windows Activation Key</Label>
                      <Input value={formData.windows_activation_key} onChange={(e) => setFormData({ ...formData, windows_activation_key: e.target.value })} />
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setCreateDialogOpen(false); setEditDialogOpen(false); resetForm(); }}>Cancel</Button>
            <Button onClick={editDialogOpen ? handleEdit : handleCreate} disabled={formLoading}>
              {formLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {editDialogOpen ? 'Save Changes' : 'Create Asset'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Asset</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete "{selectedAsset?.asset_name}"? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
            <Button variant="destructive" onClick={handleDelete} disabled={formLoading}>
              {formLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk Delete Dialog */}
      <Dialog open={bulkDeleteDialogOpen} onOpenChange={setBulkDeleteDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-amber-500" />
              Delete Multiple Assets
            </DialogTitle>
            <DialogDescription>
              Are you sure you want to delete {selectedAssets.length} selected asset(s)? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <div className="border rounded-lg p-4 bg-amber-50 max-h-[200px] overflow-y-auto">
            <p className="text-sm font-medium text-amber-800 mb-2">Assets to be deleted:</p>
            <div className="space-y-1">
              {selectedAssets.map(id => {
                const asset = assets.find(a => a.asset_id === id);
                return (
                  <div key={id} className="text-sm text-amber-700">
                    • {id} - {asset?.asset_name || 'Unknown'}
                  </div>
                );
              })}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setBulkDeleteDialogOpen(false)}>Cancel</Button>
            <Button variant="destructive" onClick={handleBulkDelete} disabled={formLoading}>
              {formLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Delete {selectedAssets.length} Assets
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Allocate Dialog */}
      <Dialog open={allocateDialogOpen} onOpenChange={setAllocateDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Allocate Asset</DialogTitle>
            <DialogDescription>Assign an asset to an employee</DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="space-y-2">
              <Label>Asset</Label>
              <Select value={allocateData.asset_id} onValueChange={(v) => setAllocateData({ ...allocateData, asset_id: v })}>
                <SelectTrigger><SelectValue placeholder="Select asset" /></SelectTrigger>
                <SelectContent>
                  {unallocatedAssets.map(a => (
                    <SelectItem key={a.asset_id} value={a.asset_id}>{a.asset_id} - {a.asset_name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Employee</Label>
              <Select value={allocateData.employee_id} onValueChange={(v) => setAllocateData({ ...allocateData, employee_id: v })}>
                <SelectTrigger><SelectValue placeholder="Select employee" /></SelectTrigger>
                <SelectContent>
                  {Array.isArray(employees) && employees.filter(e => e.employee_status === 'Employed').map(e => (
                    <SelectItem key={e.employee_id} value={e.employee_id}>{e.employee_name} ({e.employee_id})</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAllocateDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleAllocate} disabled={formLoading}>
              {formLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Allocate
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Asset Update History Dialog (full audit log) */}
      <Dialog open={historyDialogOpen} onOpenChange={setHistoryDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh]">
          <DialogHeader>
            <DialogTitle>Asset Update History</DialogTitle>
            <DialogDescription>
              Lifecycle history for {selectedAsset?.asset_id} - {selectedAsset?.asset_name}
            </DialogDescription>
          </DialogHeader>
          <ScrollArea className="h-[400px] pr-4">
            {historyLoading ? (
              <div className="space-y-2">
                {[1, 2, 3, 4, 5].map(i => <Skeleton key={i} className="h-16 w-full" />)}
              </div>
            ) : historyData.length === 0 ? (
              <div className="text-center py-12 text-muted-foreground">
                No history found for this asset.
              </div>
            ) : (
              <div className="space-y-3">
                {historyData.map((log, idx) => (
                  <div key={idx} className="border rounded-lg p-3">
                    <div className="flex items-center justify-between mb-1">
                      <Badge variant={
                        log.action?.includes('CREATED') ? 'default' :
                        log.action?.includes('ALLOCATED') ? 'secondary' :
                        log.action?.includes('UNALLOCATED') ? 'outline' :
                        log.action?.includes('UPDATED') ? 'default' : 'outline'
                      }>
                        {log.action}
                      </Badge>
                      <span className="text-xs text-muted-foreground">
                        {new Date(log.timestamp).toLocaleString()}
                      </span>
                    </div>
                    <p className="text-sm">{log.details}</p>
                    <p className="text-xs text-muted-foreground mt-1">By: {log.username}</p>
                  </div>
                ))}
              </div>
            )}
          </ScrollArea>
          <DialogFooter>
            <Button variant="outline" onClick={() => setHistoryDialogOpen(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Allocation History Dialog (allocations only) */}
      <Dialog open={allocHistoryDialogOpen} onOpenChange={setAllocHistoryDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh]">
          <DialogHeader>
            <DialogTitle>Allocation History</DialogTitle>
            <DialogDescription>
              Employees who used {selectedAsset?.asset_id} - {selectedAsset?.asset_name}
            </DialogDescription>
          </DialogHeader>
          <ScrollArea className="h-[400px] pr-4">
            {allocHistoryLoading ? (
              <div className="space-y-2">
                {[1, 2, 3].map(i => <Skeleton key={i} className="h-14 w-full" />)}
              </div>
            ) : allocHistoryData.length === 0 ? (
              <div className="text-center py-12 text-muted-foreground" data-testid="asset-list-alloc-history-empty">
                This asset has never been allocated.
              </div>
            ) : (
              <div className="space-y-2" data-testid="asset-list-alloc-history-list">
                {allocHistoryData.map((row, idx) => (
                  <div
                    key={idx}
                    className="border rounded-lg p-3 flex items-center justify-between flex-wrap gap-3"
                    data-testid={`asset-list-alloc-history-row-${idx}`}
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      <UserIcon className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                      {row.employee_id && onView && selectedAsset ? (
                        <button
                          type="button"
                          className="text-sm font-medium text-primary hover:underline text-left"
                          onClick={() => {
                            // Close dialog and open the asset detail; the user can drill further into the employee from there
                            setAllocHistoryDialogOpen(false);
                            onView(selectedAsset);
                          }}
                          data-testid={`asset-list-alloc-history-employee-link-${idx}`}
                          title="Open asset detail to view this employee"
                        >
                          {row.employee_name || row.employee_id}
                        </button>
                      ) : (
                        <span className="text-sm font-medium">
                          {row.employee_name || row.employee_id || <span className="italic text-muted-foreground">Unknown</span>}
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-3 flex-wrap text-xs text-muted-foreground">
                      <span className="inline-flex items-center gap-1">
                        <Calendar className="h-3 w-3" />
                        {row.allocated_at ? new Date(row.allocated_at).toLocaleString() : '—'}
                      </span>
                      <Badge variant={row.status === 'Active' ? 'default' : 'outline'}>
                        {row.status}
                      </Badge>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </ScrollArea>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAllocHistoryDialogOpen(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Enhanced Import Dialog */}
      <Dialog open={importDialogOpen} onOpenChange={setImportDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[85vh]">
          <DialogHeader>
            <DialogTitle>Import Assets</DialogTitle>
            <DialogDescription>
              {importStep === 'info' && 'Review the required columns and download a template'}
              {importStep === 'preview' && `${importPreview.valid.length} valid, ${importPreview.invalid.length} invalid records`}
            </DialogDescription>
          </DialogHeader>

          {importStep === 'info' && (
            <div className="space-y-4">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-3">
                  <Info className="h-5 w-5 text-blue-600" />
                  <h4 className="font-medium text-blue-800">Excel File Requirements</h4>
                </div>
                <p className="text-sm text-blue-700 mb-3">
                  Your Excel file should have the following columns in the first row (header row):
                </p>
              </div>

              <ScrollArea className="h-[300px] pr-4">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Column Name</TableHead>
                      <TableHead>Required</TableHead>
                      <TableHead>Description</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {importColumns.map((col) => (
                      <TableRow key={col.name}>
                        <TableCell className="font-mono text-sm">{col.name}</TableCell>
                        <TableCell>
                          {col.required ? (
                            <Badge variant="destructive" className="text-xs">Required</Badge>
                          ) : (
                            <Badge variant="secondary" className="text-xs">Optional</Badge>
                          )}
                        </TableCell>
                        <TableCell className="text-sm text-muted-foreground">{col.description}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </ScrollArea>

              <div className="flex items-center justify-between pt-4 border-t">
                <Button variant="outline" onClick={handleDownloadTemplate} disabled={formLoading} className="gap-2">
                  {formLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileDown className="h-4 w-4" />}
                  Download Sample Template
                </Button>
                <div className="flex gap-2">
                  <Button variant="outline" onClick={() => setImportDialogOpen(false)}>Cancel</Button>
                  <Button onClick={() => fileInputRef.current?.click()} className="gap-2">
                    <FileUp className="h-4 w-4" />
                    Select File to Upload
                  </Button>
                  <input 
                    ref={fileInputRef} 
                    type="file" 
                    accept=".xlsx,.xls" 
                    className="hidden" 
                    onChange={handleFileSelect} 
                  />
                </div>
              </div>
            </div>
          )}

          {importStep === 'preview' && (
            <div className="space-y-4">
              <ScrollArea className="h-[400px] pr-4">
                {importPreview.invalid.length > 0 && (
                  <div className="mb-4">
                    <h4 className="font-medium text-red-600 mb-2">Invalid Records:</h4>
                    {importPreview.invalid.slice(0, 5).map((r, i) => (
                      <div key={i} className="text-sm bg-red-50 p-2 rounded mb-1">
                        Row {r.row}: {r.errors.join(', ')}
                      </div>
                    ))}
                    {importPreview.invalid.length > 5 && (
                      <p className="text-sm text-red-500 mt-2">
                        ... and {importPreview.invalid.length - 5} more invalid records
                      </p>
                    )}
                  </div>
                )}
                {importPreview.valid.length > 0 && (
                  <div>
                    <h4 className="font-medium text-green-600 mb-2">Valid Records ({importPreview.valid.length} total):</h4>
                    <div className="rounded-md border overflow-x-auto">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Asset ID</TableHead>
                            <TableHead>Name</TableHead>
                            <TableHead>Type</TableHead>
                            <TableHead>Status</TableHead>
                            <TableHead>Price</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {importPreview.valid.slice(0, 10).map((r, i) => (
                            <TableRow key={i}>
                              <TableCell className="font-mono">{r.asset_id}</TableCell>
                              <TableCell>{r.asset_name}</TableCell>
                              <TableCell>{r.asset_type}</TableCell>
                              <TableCell>{r.status}</TableCell>
                              <TableCell>{r.price || '-'}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                    {importPreview.valid.length > 10 && (
                      <p className="text-sm text-muted-foreground mt-2">
                        Showing first 10 of {importPreview.valid.length} valid records
                      </p>
                    )}
                  </div>
                )}
              </ScrollArea>

              <DialogFooter className="border-t pt-4">
                <Button variant="outline" onClick={() => setImportStep('info')}>Back</Button>
                <Button variant="outline" onClick={() => setImportDialogOpen(false)}>Cancel</Button>
                <Button onClick={handleImport} disabled={formLoading || importPreview.valid.length === 0}>
                  {formLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  Import {importPreview.valid.length} Assets
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AssetList;
